import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useOptionalAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/api';
import { subscribe as subscribeToSettingsCache, QUOTE_DEFAULTS_SIGNAL, COMPANY_PROFILE_SIGNAL, BRANDING_SIGNAL } from '../services/settingsListCache';
import {
  VOCAB_SEEDS,
  resolveSetKey,
  getSeedValues,
  findSeedLabel,
  prettifyEnum,
} from '../constants/vocabSeeds';

/**
 * useSettings — Hook to bind form dropdowns to Settings values (REQ-DEP-01)
 *
 * Every dropdown in the app binds to Settings (default/custom/hidden/deleted).
 * - Add a value in Settings → it appears in the consuming form
 * - Hide a default → it dims in the dropdown (not selectable for new items)
 * - Delete a custom → removed from future dropdowns (existing records keep the value)
 *
 * Usage:
 *   const { getOptions, getEnumLabel, loading } = useSettings();
 *   const leadSources = getOptions('lead_sources');
 *   const paymentTermLabel = getEnumLabel('payment_terms', quote.payment_term,
 *                                         { quoteDefaultsField: 'payment_terms' });
 *
 * PR-B1 (2026-07-21) refactor:
 * - Local `DEFAULTS` map retired. Canonical default values now sourced from
 *   `constants/vocabSeeds.js` — the single `definition_home` for all vocab
 *   seeds. Adding a shadow copy of any set here is a merge block.
 * - Added `getEnumLabel(listType, value, opts)` implementing the 4-step
 *   render-precedence chain (see docstring). Consumers rendering an enum
 *   value on a customer-visible surface MUST use this helper — never render
 *   the raw value.
 * - `settingsAPI.getQuoteDefaults()` is now loaded alongside the list types
 *   so step-3 tenant-default fallback is available synchronously to renders.
 */

// ─── Settings list types (canonical keys) ────────────────────────────────────
export const SETTINGS_LISTS = {
  LEAD_SOURCES: 'lead_sources',
  JOB_TYPES: 'job_types',
  WORK_ORDER_TYPES: 'work_order_types',
  CUSTOMER_TYPES: 'customer_types',
  SALESPERSONS: 'salespersons',
  AREAS: 'areas',
  SUB_AREAS: 'sub_areas',
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  PAYMENT_TERMS: 'payment_terms',
  PAYMENT_TYPES: 'payment_types',
  TC_TEMPLATES: 'tc_templates',
  CREWS: 'crews',
  RESOURCE_TYPES: 'resource_types',
  COMPETITORS: 'competitors',
  CONTACT_ROLES: 'contact_roles', // PR-B1 (2026-07-21): promoted from CustomerFormPanel hardcode
  // IDI-153 (2026-09-28): offices must be FETCHED, not just aliased in
  // vocabSeeds. getEnumLabel resolves against this hook's cache, so a list
  // type missing here silently returns the RAW VALUE -- the customer record
  // rendered 'Office: 1' instead of 'Office: Main' on the deployed build.
  OFFICES: 'offices',
  // IDI-406: Mark as Lost offers the dealer's own (migrated) lost reasons.
  LOST_REASONS: 'lost_reasons',
};

// ─── Categories default (not a vocabSeeds set — trade-specific product cats)
// Kept as a local default because `categories` is inventory/trade-catalog
// scoped, not a taxonomy in the same sense as the other sets. If this needs
// to become a Seed Registry entry later, move it to vocabSeeds.js.
const LOCAL_DEFAULTS = {
  categories: [
    { value: 'insulation',   label: 'Insulation' },
    { value: 'drywall',      label: 'Drywall' },
    { value: 'fireproofing', label: 'Fireproofing' },
    { value: 'acoustical',   label: 'Acoustical' },
    { value: 'spray_foam',   label: 'Spray Foam' },
    { value: 'custom',       label: 'Custom Item' },
  ],
};

// Return the canonical default options for a list_type. Consults vocabSeeds
// first (via the plural→singular alias), falls back to LOCAL_DEFAULTS for
// anything not in the Seed Registry, returns [] if neither has it (allowed —
// means "tenant-specific, add via Settings").
function defaultsFor(listType) {
  const setKey = resolveSetKey(listType);
  if (setKey && VOCAB_SEEDS[setKey] && Array.isArray(VOCAB_SEEDS[setKey].values)) {
    return getSeedValues(setKey);
  }
  return LOCAL_DEFAULTS[listType] || [];
}

/**
 * Hook: useSettings
 * Fetches and caches settings lists. Returns getOptions() to bind dropdowns
 * and getEnumLabel() to resolve a single value to a display label.
 */
export default function useSettings() {
  // IDI-304: settings lists belong to ONE signed-in identity. SettingsProvider
  // sits above the router and used to load once per page load, so after a
  // logout -> login as another dealer the previous dealer's crews / job types
  // stayed in state until a hard reload. Re-run the load when the identity
  // changes, and skip it entirely while signed out (a load there only
  // produces 401s). Outside an AuthProvider (unit tests) auth is null and we
  // keep the old always-load behaviour.
  const auth = useOptionalAuth();
  const identity = auth
    ? (auth.isAuthenticated && auth.user ? `${auth.user.tenantId}::${auth.user.id}` : null)
    : 'no-auth-provider';
  const [cache, setCache] = useState({});
  const [errors, setErrors] = useState({}); // PR-42: per-listType errors, keyed by listType
  const [quoteDefaults, setQuoteDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  // IDI-714: read by the cache subscriber without resubscribing on sign-in.
  const identityRef = useRef(identity);
  identityRef.current = identity;

  // Fetch a specific list type from the API.
  //
  // PR-42 (2026-08-18): previously substituted `defaultsFor(listType)` on
  // rejection — a Family C swallow that rendered the bundled seed vocabulary
  // as if it were the tenant's data. Now: capture the error into per-listType
  // `errors` state and return []. Dropdowns still see [] (their surface can't
  // render an error), but consumers that DO care can call `getError(listType)`
  // and show an alert above the dropdown.
  const fetchList = useCallback(async (listType) => {
    try {
      const items = await settingsAPI.getList(listType);
      setCache(prev => ({ ...prev, [listType]: items }));
      setErrors(prev => {
        if (!prev[listType]) return prev; // no-op if there was no prior error
        const next = { ...prev };
        delete next[listType];
        return next;
      });
      return items;
    } catch (err) {
      // Log the REAL error (not a swallowed "using defaults" warning) so a
      // 500 or a 401 is visible to whoever opens devtools.
      console.error(`Settings fetch failed for ${listType}:`, err);
      setErrors(prev => ({ ...prev, [listType]: err }));

      // PR-74 (dispatch 443 §3.1): CLEAR the cache entry on failed
      // re-fetch, do NOT retain the previous value.
      //
      // Before PR-74 refresh() had zero callers, so a fetchList failure
      // was unreachable outside tests — and the retained-stale behaviour
      // was harmless. PR-74 gives refresh() a caller on every settings
      // write (via settingsListCache.subscribe). Without this clear, the
      // sequence "user saves customer type → refresh fails → cache
      // retains pre-write value" would render the OLD label confidently
      // with no signal — the exact defect IDI-83 exists to close,
      // re-entering through the error path.
      //
      // Clearing (undefined, not []) puts the reader in the same state
      // as a failed mount load: cache[listType] is missing, and
      // errors[listType] carries the reason. One failure shape instead
      // of two, and getError() still distinguishes it from a genuine
      // empty list. Do NOT write [] here — that would manufacture
      // "the tenant has no <listType>," which is a claim.
      setCache(prev => {
        if (!(listType in prev)) return prev;
        const next = { ...prev };
        delete next[listType];
        return next;
      });
      return [];
    }
  }, []);

  // Load all common lists + quote defaults on mount, and again whenever the
  // signed-in identity changes (IDI-304).
  useEffect(() => {
    let cancelled = false;
    // Never show the previous identity's lists while the next one loads.
    setCache({});
    setErrors({});
    setQuoteDefaults(null);
    if (!identity) {
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);

    const loadAll = async () => {
      const types = Object.values(SETTINGS_LISTS);
      const [listResults, quoteDefaultsResult] = await Promise.all([
        Promise.allSettled(
          types.map(type => settingsAPI.getList(type))
        ),
        settingsAPI.getQuoteDefaults().then(r => r?.data ?? r).catch(err => {
          console.error('Settings initial-load quoteDefaults failed:', err);
          return null;
        }),
      ]);

      if (cancelled) return;

      const newCache = {};
      const newErrors = {};
      types.forEach((type, i) => {
        const result = listResults[i];
        if (result.status === 'fulfilled' && result.value) {
          newCache[type] = result.value;
        } else if (result.status === 'rejected') {
          console.error(`Settings initial-load failed for ${type}:`, result.reason);
          newErrors[type] = result.reason;
        }
      });
      setCache(newCache);
      setErrors(newErrors);
      setQuoteDefaults(quoteDefaultsResult || null);
      setLoading(false);
    };

    loadAll();
    return () => { cancelled = true; };
  }, [identity]);

  /**
   * Get options for a dropdown.
   * Merges canonical vocab seeds (from vocabSeeds.js) + custom items from Settings.
   * Filters out hidden items (they dim in Settings UI but don't appear in forms).
   */
  const getOptions = useCallback((listType, opts = {}) => {
    const { includeHidden = false } = opts;
    const defaults = defaultsFor(listType);
    const apiItems = cache[listType] || [];

    const merged = new Map();

    // IDI-399: if the tenant has ANY rows for this list, their list IS the
    // list — the canonical seed defaults do NOT appear. This is the single
    // root cause behind every "extras" complaint (Sarah, IDI-293, IDI-387,
    // IDI-381): the merge always seeded 4 canonical customer types / 5 lead
    // sources / etc. on top of the tenant's own migrated rows, so the picker
    // showed both the seed's generic entries AND the tenant's real ones.
    // Seed defaults only appear for a tenant with NO rows (a genuinely
    // unconfigured list, where the seeds are the only reasonable fallback).
    const tenantHasOwnRows = apiItems.length > 0;

    // Seed with canonical defaults ONLY when the tenant has no rows.
    if (!tenantHasOwnRows) {
      defaults.forEach(d => {
        merged.set(d.value, { ...d, isDefault: true, hidden: false });
      });
    }

    // Overlay API items (may hide defaults, add custom items, delete customs)
    apiItems.forEach(item => {
      // PR-B2b-part-1.1 (2026-07-21): derive merge key as `value` → name-slug →
      // id (in that order). Previously we tried `id` before `name`, which
      // caused tenant-seeded rows (with UUID `id`s but matching human names)
      // to fail to coalesce with the canonical seed — producing duplicate
      // entries in dropdowns. See ledger item "useSettings.getOptions merge
      // doubles values when seeded via {name} without explicit {value}".
      const nameSlug = item.name?.toLowerCase().replace(/\s+/g, '_');
      const value = item.value || nameSlug || item.id;
      const label = item.label || item.name || item.value;
      // IDI-399 (09-29 call): a row the dealer marked INACTIVE in Settings is
      // not offered in any picker. Before this, Sample Dealer's inactive
      // migrated "Commercial" (id 1, active:false) sat beside the active one
      // and the picker read "Commercial" twice. `hidden` keeps it reachable
      // via includeHidden, so labels on OLD records still resolve.
      const hidden = item.hidden || item.status === 'hidden' || item.active === false || false;
      const deleted = item.deleted || item.status === 'deleted' || false;

      if (deleted) {
        merged.delete(value);
        return;
      }

      // IDI-293 (2026-09-16): a MIGRATED item's `value` is the legacy numeric
      // id (e.g. "1"), never the seed's semantic slug ("commercial") — so the
      // priority above (item.value first) never coalesced with a matching
      // seed row. Both survived as separate entries: the picker showed the
      // seed's 4 canonical types (2 of which, "Residential Remodel"/
      // "Commercial Remodel", don't even exist for this tenant) PLUS the
      // tenant's real migrated types — 7 items, membership mismatched from
      // Settings' 3. If nameSlug matches an EXISTING seed key that item.value
      // does not, coalesce there instead of creating a second entry.
      const mergeKey = (!merged.has(value) && nameSlug && merged.has(nameSlug) && merged.get(nameSlug).isDefault)
        ? nameSlug
        : value;

      // FP2-F3: preserve auxiliary render fields (e.g., crew color swatch, tech
      // avatar hint) so consumers rendering identity chips don't have to source
      // the same records a second time via useSettingsList. Additive only —
      // known-safe passthrough of common fields; extend as needed.
      const aux = {};
      if (item.color != null) aux.color = item.color;
      if (item.swatch != null) aux.swatch = item.swatch;

      if (merged.has(mergeKey)) {
        merged.set(mergeKey, {
          ...merged.get(mergeKey),
          ...aux,
          hidden,
          // Prefer the tenant's real label/value over the seed's when
          // coalescing into a seed slot — the migrated row is the ground
          // truth once it exists.
          value,
          label: label || merged.get(mergeKey).label,
          isDefault: false,
        });
      } else {
        merged.set(mergeKey, { value, label, ...aux, hidden, custom: true });
      }
    });

    const result = Array.from(merged.values());
    if (includeHidden) return result;
    // IDI-399: one entry per NAME in a picker. The dealer's list can carry
    // the same name twice (a migrated row "Residential" id 2 AND a seeded row
    // "Residential" id b7906b46, both active on Sample Dealer), which the
    // user sees as a duplicate. Keep the first visible one (the order the
    // tenant's list is stored in). Values stay distinct in storage; only the
    // picker collapses.
    const seen = new Set();
    return result.filter((item) => {
      if (item.hidden) return false;
      const key = String(item.label || item.value || '').trim().toLowerCase();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [cache]);

  /**
   * getEnumLabel(listType, value, opts) — resolve a single enum value to a
   * display label using the canonical 4-step precedence chain:
   *
   *   1. Live settings list match (tenant-scoped; via `cache[listType]`)
   *   2. Canonical seed match (vocabSeeds.js)
   *   3. Tenant's Quote Defaults value for `opts.quoteDefaultsField`
   *      (recursively resolved via steps 1→2 so it prints a proper label,
   *      not a raw value)
   *   4. `prettifyEnum(value)` cosmetic fallback (snake_case → Title Case)
   *
   * ALWAYS use this helper on customer-visible surfaces. Never render a raw
   * enum value; that's the PR-4 leak class of bug this helper prevents.
   *
   * @param {string} listType   - e.g. 'payment_types', 'contact_roles'
   * @param {string} value      - the enum value to resolve, e.g. 'net_30'
   * @param {object} [opts]
   * @param {string} [opts.quoteDefaultsField]
   *        - Field name on the tenant's Quote Defaults record to consult for
   *          step-3 fallback. Typical: 'payment_type', 'payment_terms'.
   *          Omit if there's no sensible tenant default for this field.
   * @param {boolean} [opts.emptyAsBlank=true]
   *        - If true, return '' for null/undefined/empty input (default).
   *          If false, return the input unchanged (useful for form fields).
   * @returns {string} the resolved label, or a cosmetic fallback, or ''
   */
  const getEnumLabel = useCallback((listType, value, opts = {}) => {
    const { quoteDefaultsField, emptyAsBlank = true } = opts;

    if (value == null || value === '') return emptyAsBlank ? '' : value;

    const needle = String(value).toLowerCase();

    // Step 1 — Live settings list
    const apiItems = cache[listType] || [];
    for (const item of apiItems) {
      const itemValue = item.value || item.name?.toLowerCase().replace(/\s+/g, '_') || item.id;
      const itemLabel = item.label || item.name || item.value;
      if (String(itemValue).toLowerCase() === needle && itemLabel) {
        return itemLabel;
      }
    }
    // IDI-495: a stored value may be the row's ID (IDI-378 / Q-21 rewrites
    // stored values to row ids; the edit form saves whatever SelectMenu
    // emits) or its NAME in another case/spacing ("Net 30" vs "net_30"). Match
    // both, so a reader never shows the raw id.
    const slug = (v) => String(v ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
    for (const item of apiItems) {
      const itemLabel = item.label || item.name || item.value;
      if (!itemLabel) continue;
      if (item.id != null && String(item.id).toLowerCase() === needle) return itemLabel;
      if (item.legacyId != null && String(item.legacyId).toLowerCase() === needle) return itemLabel;
      if (slug(item.name) === slug(value) || slug(item.label) === slug(value)) return itemLabel;
    }

    // Step 2 — Canonical seed (vocabSeeds.js)
    const seedLabel = findSeedLabel(listType, value);
    if (seedLabel) return seedLabel;

    // Step 3 — Tenant Quote Defaults fallback (if caller supplied a field to check)
    if (quoteDefaultsField && quoteDefaults && quoteDefaults[quoteDefaultsField]) {
      const tenantDefault = quoteDefaults[quoteDefaultsField];
      // Resolve the tenant default itself via steps 1 → 2 (no recursion into step 3
      // to guarantee termination + avoid infinite loops on misconfigured defaults).
      const tenantDefaultLower = String(tenantDefault).toLowerCase();
      for (const item of apiItems) {
        const itemValue = item.value || item.name?.toLowerCase().replace(/\s+/g, '_') || item.id;
        const itemLabel = item.label || item.name || item.value;
        if (String(itemValue).toLowerCase() === tenantDefaultLower && itemLabel) {
          return itemLabel;
        }
      }
      const seedLabelForDefault = findSeedLabel(listType, tenantDefault);
      if (seedLabelForDefault) return seedLabelForDefault;
    }

    // Step 4 — Cosmetic fallback
    return prettifyEnum(value);
  }, [cache, quoteDefaults]);

  /**
   * Refresh a specific list from the API.
   */
  const refresh = useCallback(async (listType) => {
    if (listType) {
      await fetchList(listType);
    } else {
      const types = Object.values(SETTINGS_LISTS);
      await Promise.allSettled(types.map(fetchList));
      // Also refresh quote defaults. PR-42: fail LOUD, not silent — if the
      // endpoint is broken we want to see the error in the console instead
      // of pretending the refresh succeeded.
      try {
        const qd = await settingsAPI.getQuoteDefaults().then(r => r?.data ?? r);
        setQuoteDefaults(qd || null);
      } catch (err) {
        console.error('Settings quoteDefaults refresh failed:', err);
      }
    }
  }, [fetchList]);

  // PR-74 (IDI-83, dispatch 443): subscribe to settingsListCache
  // invalidations. Every settings-list writer (addListItem,
  // updateListItem, deleteListItem, reorderList) already calls
  // invalidate(listType) on success — verified by the earlier suite in
  // services/settingsListCache.test.js. Before PR-74 nothing consumed
  // that signal, so mounted consumers (getEnumLabel/getOptions) rendered
  // pre-write labels until a full page reload.
  //
  // The listener re-fetches ONLY the invalidated listType (per dispatch
  // 442 §3 ruling: a settings write should cost one request, not the
  // mount-time fan-out). listType=undefined means "the whole cache is
  // toast" — refresh() with no argument re-fetches all and quote
  // defaults, matching mount-time behaviour.
  //
  // Unsubscribe on unmount is mandatory: if the provider re-mounts
  // (tenant switch, route replay), the old subscriber would still be
  // registered against a stale setCache closure and cause spurious
  // state updates on an unmounted component. The unsubscribe returned
  // by subscribe() is idempotent (settingsListCache.test.js).
  // IDI-714: a Quote Defaults save re-reads Quote Defaults only (one
  // request, no list). A failed read keeps the previous copy (logged);
  // signed out, nothing is requested.
  const refreshQuoteDefaults = useCallback(async () => {
    const requestedFor = identityRef.current;
    if (!requestedFor) return;
    try {
      const qd = await settingsAPI.getQuoteDefaults().then(r => r?.data ?? r);
      if (identityRef.current !== requestedFor) return;
      setQuoteDefaults(qd || null);
    } catch (err) {
      console.error('Settings quoteDefaults refresh failed:', err);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToSettingsCache((listType) => {
      if (listType === QUOTE_DEFAULTS_SIGNAL) {
        refreshQuoteDefaults();
        return;
      }
      // IDI-718: those copies live in useCompanyProfile / useBranding.
      if (listType === COMPANY_PROFILE_SIGNAL || listType === BRANDING_SIGNAL) return;
      refresh(listType);
    });
    return unsubscribe;
  }, [refresh, refreshQuoteDefaults]);

  // PR-42: getError(listType) exposes the per-listType error captured by
  // fetchList. Undefined means "no error." Consumers that want to render
  // the ERROR state distinctly from EMPTY use this.
  const getError = useCallback(
    (listType) => errors[listType],
    [errors]
  );

  return { getOptions, getEnumLabel, getError, loading, refresh, cache, quoteDefaults, errors };
}

// ─── Context (for app-wide access without prop drilling) ─────────────────────
const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const settings = useSettings();
  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettingsContext() {
  const ctx = useContext(SettingsContext);
  // If no provider, return a minimal fallback (won't have cached data)
  if (!ctx) {
    return {
      getOptions: () => [],
      getEnumLabel: (_lt, v) => (v == null || v === '' ? '' : prettifyEnum(v)),
      getError: () => undefined,
      loading: false,
      refresh: async () => {},
      cache: {},
      quoteDefaults: null,
      errors: {},
    };
  }
  return ctx;
}
